/**
 * createApp: bootstrap a PlayCanvas Application.
 *
 * Adapted from supersplat-viewer/src/index.ts (createApp / initCanvas), simplified:
 * WebGPU-preferred graphics device with WebGL2 fallback, standard Application
 * (auto-registers GSplat system/handler), camera rig (PlayerRoot + Camera child),
 * directional light, fixed canvas sizing.
 */

import {
    Application,
    Color,
    DEVICETYPE_WEBGPU,
    DEVICETYPE_WEBGL2,
    Entity,
    GSPLAT_RENDERER_RASTER_GPU_SORT,
    RESOLUTION_FIXED,
    createGraphicsDevice
} from 'playcanvas';
import type { GraphicsDevice } from 'playcanvas';
import { eventBus } from '../events/EventBus';
import { installGsplatEnginePatches } from '../scene/gsplatEnginePatch';
import { initLayers } from '../render/LayerManager';
import { markStableSession, STABLE_SESSION_MS, mobileTierLevel, mobileDegradeActive } from './stabilityGuard';

export interface AppContext {
    app: Application;
    /** PlayerRoot entity — holds the player position. */
    cameraRoot: Entity;
    /** Camera entity (child of cameraRoot) — holds the camera rotation. */
    camera: Entity;
}

// ── WebGPU 兼容性守卫 ─────────────────────────────────────────────────────
// 三层决策（2026-10-14 手机误伤案例后定型：支持检测 → 瞬态重试 → 二连降级）：
//   ① 静态支持检测：navigator.gpu 缺失 / requestAdapter 拿不到 adapter →
//      直接 WebGL2。不支持≠坏：不落 flag、不给兼容提示，每次 boot 重测
//      （requestAdapter 毫秒级），浏览器升级出 WebGPU 后下次访问自然启用。
//   ② 支持则 WebGPU 启动并装运行期健康守卫；首次触发（错误窗/慢性
//      devicelost）只记 sessionStorage「trip」并整页重载重试一次——瞬态
//      抖动（场景加载期显存压力 devicelost、睡眠唤醒）重载后即恢复，
//      不产生任何降级（当天手机 20帧→10帧案例：WebGPU 本身好的，一次
//      抖动被直接钉死 WebGL2 半速一整天）。
//   ③ 30 分钟内二连触发才落 24h flag 进 WebGL2 兼容模式（带用户提示）。
//      真坏的机器重载后几秒内必再触发，二连即中；偶发的永远到不了第三层。
//      ?renderer=webgpu 立即清除重试，?renderer=webgl2 手动强制（排障用）。
//      （2026-10-17 触发条件语义修正：进入②③的「触发」本身收紧为「渲染
//      实际坏掉」——错误爆发须连续持续 ≥10s、devicelost 只认未恢复或密集
//      风暴，引擎自愈的孤立丢失与一过性爆发不再触发，见下方常量注释。）
//   ④ 强开验证白名单（2026-10-17 Ampere 桌面案例）：?renderer=webgpu 会话
//      稳定跑满 10 分钟 = 人工验证本机 WebGPU 实际可用，7 天内守卫触发
//      只记录不降级不重载——错误计数分不清「破坏画面的坏管线」和「可自愈
//      的偶发抖动」，强开 10 分钟可以。
// 2026-10-14 案例：某台电脑能初始化 WebGPU（navigator.gpu 在、adapter 请求
// 成功，WebGL2 兜底不触发），但高斯排序的 work-buffer 管线在该机驱动上编译
// 失败——控制台每帧刷 "Invalid RenderPipeline ... SC-PassEncoder
// RT:GsplatWorkBuffer-MRT-0"（SC=minify 后的 GSplatWorkBufferRenderPass），
// Invalid CommandBuffer 导致画面不可用 + 浏览器闪。设备创建阶段查不出这种
// 驱动级失败，只能运行期探测：uncapturederror（每帧编码错误必然触发）或
// 慢性 devicelost（引擎会自动重建设备，反复丢失=闪屏）。引擎侧 gsplat
// GPU_SORT 在 WebGL2 自动回落 CPU 排序，全功能可用。
const WEBGPU_BROKEN_FLAG = 'nngs.webgpu-broken';
/** 随 flag 落盘的元数据 {reason, adapter, ts}：事后排障「这台机器当初为什么
 *  降级」的第一手依据（localStorage 读 nngs.webgpu-broken-info），也是 TTL
 *  过期的时钟——没有 ts 的 flag 一律视为陈旧。 */
const WEBGPU_BROKEN_INFO = 'nngs.webgpu-broken-info';
/** flag 有效期 24h：过期自动清除并重试 WebGPU。真坏的机器几秒内会带着原因
 *  再次触发（代价=每过一个 TTL 多一次重载）；被误伤的健康机器（睡眠/唤醒
 *  等偶发 devicelost）一天内自愈回 WebGPU，不再永久降级（2026-10-14「所有
 *  电脑都是 WebGL2」的教训：粘性 flag + 无窗累计把偶发事件放大成全员工伤）。 */
const WEBGPU_BROKEN_TTL_MS = 24 * 60 * 60 * 1000;
/** 瞬态重试记录（sessionStorage，随标签页存活、reload 保留）：运行期守卫
 *  首次触发写入，30 分钟内再次触发才升级为 24h flag。用 sessionStorage 而
 *  非 localStorage——「本次浏览会话内的二连」才是慢性故障信号，昨天的
 *  抖动不该影响今天。 */
const WEBGPU_TRIP_KEY = 'nngs.webgpu-trip';
const WEBGPU_TRIP_WINDOW_MS = 30 * 60 * 1000;
/** 滑动窗口阈值：10s 内 ≥8 个未捕获 GPU 校验/内部错误算「一波爆发」。坏管线
 *  每帧报错（60FPS 下 ~0.13s 攒满），健康机器的引擎发布版不应出现。 */
const GPU_ERROR_WINDOW_MS = 10000;
const GPU_ERROR_THRESHOLD = 8;
/** 错误爆发只认「连续不断」（2026-10-17 Ampere 案例定稿）：首窗达标只挂起，
 *  静默满一个窗（10s 无错误）挂起即作废、下波重新起算；只有错误流不带
 *  ≥10s 间隙地续满 10s 才确认降级。坏管线每帧报错，~10s 必过线；加载/切换
 *  期的一过性爆发（哪怕隔 40s 再来一波）各自重新起算，拼不成「持续」——
 *  「有错误」不等于「渲染坏了」。 */
/** devicelost 只按影响判（2026-10-17 Ampere 案例定稿）：WebGPU 上引擎对每次
 *  丢失都会自动重建设备（handleDeviceLost→createDevice→devicerestored）并
 *  继续渲染，孤立的自愈丢失（重场景加载的显存压力、睡眠唤醒、驱动 TDR）
 *  不该计入——旧规则「10 分钟 ≥2 次」把两次无关的加载压力拼成「慢性崩溃」，
 *  是当天正常渲染的 Ampere 桌面被误降 24h 的根源。只认两种真坏：
 *  ① 丢了 10s 没恢复（设备重建失败，渲染已死）；② 60s 内丢 ≥3 次
 *  （恢复再丢的风暴 = 用户可见的闪屏地狱）。 */
const DEVICE_RESTORE_GRACE_MS = 10000;
const DEVICE_LOST_WINDOW_MS = 60000;
const DEVICE_LOST_LIMIT = 3;

/** 引擎 WebgpuGraphicsDevice 的原始 GPUDevice（属性名不被 minify，见部署包验证）。 */
interface RawGpuDevice {
    addEventListener?: (type: 'uncapturederror', listener: (ev: { error?: { constructor?: { name?: string }; message?: string } }) => void) => void;
}

function webgpuBrokenFlag(): boolean {
    try {
        if (localStorage.getItem(WEBGPU_BROKEN_FLAG) !== '1') return false;
        let ts = 0;
        try { ts = (JSON.parse(localStorage.getItem(WEBGPU_BROKEN_INFO) ?? '{}') as { ts?: number }).ts ?? 0; } catch { /* info 损坏按无时间处理 */ }
        // 无 ts = 升级前的旧裸 flag，同样视为过期：部署本修复后，存量降级机器
        // 首次访问即自动重试 WebGPU——真坏的会再次触发（这次会带 reason 落盘），
        // 误伤的直接恢复，无需逐台清理。
        if (!ts || Date.now() - ts > WEBGPU_BROKEN_TTL_MS) {
            localStorage.removeItem(WEBGPU_BROKEN_FLAG);
            localStorage.removeItem(WEBGPU_BROKEN_INFO);
            console.info(`[gfx] WebGPU 兼容性失败标记已过期${ts ? '（超过 24h）' : '（升级前的旧标记）'}，本次重试 WebGPU`);
            return false;
        }
        return true;
    } catch { return false; }
}

function setWebgpuBrokenFlag(value: boolean, reason?: string, adapter?: string): void {
    try {
        if (value) {
            localStorage.setItem(WEBGPU_BROKEN_FLAG, '1');
            localStorage.setItem(WEBGPU_BROKEN_INFO, JSON.stringify({ reason: reason ?? 'unknown', adapter, ts: Date.now() }));
        } else {
            localStorage.removeItem(WEBGPU_BROKEN_FLAG);
            localStorage.removeItem(WEBGPU_BROKEN_INFO);
        }
    } catch { /* 隐私模式禁写：本次会话仍可经 reload 参数强制 */ }
}

/** 30 分钟内的首次触发记录；过期/不存在返回 null（当下算「首触」）。 */
function recentWebgpuTrip(): { reason: string } | null {
    try {
        const raw = sessionStorage.getItem(WEBGPU_TRIP_KEY);
        if (!raw) return null;
        const trip = JSON.parse(raw) as { ts?: number; reason?: string };
        if (!trip.ts || Date.now() - trip.ts > WEBGPU_TRIP_WINDOW_MS) return null;
        return { reason: trip.reason ?? 'unknown' };
    } catch { return null; }
}

/** 强开验证白名单（2026-10-17 桌面案例）：?renderer=webgpu 会话稳定跑满
 *  10 分钟后落盘，7 天内运行期守卫的触发只记录、不重载不降级——「强开能
 *  正常渲染」是比任何错误计数都硬的健康证据（NVIDIA Ampere 桌面：守卫
 *  30 分钟二连降级落 flag，用户强开后画面/帧率/视频链路全部正常，与
 *  10-14 手机 XWEB150 误伤同性质）。坏机器到不了 10 分钟（10-14 案例强开
 *  即花屏，用户马上关页），误准入风险低；过期后重新验证即可。 */
const WEBGPU_FORCED_OK_KEY = 'nngs.webgpu-forced-ok';
const WEBGPU_FORCED_OK_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function markWebgpuForcedOk(adapter: string): void {
    try { localStorage.setItem(WEBGPU_FORCED_OK_KEY, JSON.stringify({ ts: Date.now(), adapter })); } catch { /* 隐私模式禁写：白名单失效而已 */ }
}

function webgpuForcedOkRecent(): boolean {
    try {
        const raw = localStorage.getItem(WEBGPU_FORCED_OK_KEY);
        if (!raw) return false;
        const ts = (JSON.parse(raw) as { ts?: number }).ts ?? 0;
        if (!ts || Date.now() - ts > WEBGPU_FORCED_OK_TTL_MS) {
            if (ts) localStorage.removeItem(WEBGPU_FORCED_OK_KEY);
            return false;
        }
        return true;
    } catch { return false; }
}

/** 记录首次触发。返回 false = sessionStorage 禁写（隐私模式）——两层重试
 *  走不了，调用方按旧行为直接降级。 */
function setWebgpuTrip(reason: string): boolean {
    try {
        sessionStorage.setItem(WEBGPU_TRIP_KEY, JSON.stringify({ ts: Date.now(), reason }));
        return true;
    } catch { return false; }
}

/** WebGPU 静态支持检测（三层决策第①层）：API 在且能拿到 adapter 才算支持。
 *  requestAdapter 毫秒级，每次 boot 重测；不支持直接 WebGL2——这不算
 *  「兼容模式」（不落 flag、不给提示），浏览器将来支持了下次访问自动启用。 */
async function detectWebgpuSupport(): Promise<{ supported: boolean; reason?: string }> {
    const gpu = (navigator as Navigator & { gpu?: { requestAdapter?: () => Promise<unknown> } }).gpu;
    if (!gpu || typeof gpu.requestAdapter !== 'function') return { supported: false, reason: '无 navigator.gpu（浏览器不支持）' };
    try {
        const adapter = await gpu.requestAdapter();
        if (!adapter) return { supported: false, reason: 'requestAdapter 返回 null（驱动禁用/黑名单）' };
        return { supported: true };
    } catch (err) {
        return { supported: false, reason: `requestAdapter 抛错：${err instanceof Error ? err.message : String(err)}` };
    }
}

/** 守卫观测信标：触发/降级/豁免时打一条 404 GET，nginx access log 会带
 *  query 落盘（IP+UA 本就在日志行里）——事后 grep `/__diag/gfx-guard` 即得
 *  「哪台机器、什么阶段、什么原因」，不用远程调试读 localStorage。纯观测，
 *  失败即弃，不影响任何决策路径。 */
function gfxGuardBeacon(stage: string, detail: string): void {
    try {
        const qs = `stage=${encodeURIComponent(stage)}&detail=${encodeURIComponent(detail.slice(0, 200))}`;
        void fetch(`/__diag/gfx-guard?${qs}`, { keepalive: true }).catch(() => { /* 纯观测 */ });
    } catch { /* 纯观测 */ }
}

/** 手动渲染后端覆盖：?renderer=webgl2 强制兼容模式 / ?renderer=webgpu 清除重试。 */
function rendererOverride(): 'webgl2' | 'webgpu' | null {
    const value = new URLSearchParams(window.location.search).get('renderer');
    return value === 'webgl2' ? 'webgl2' : value === 'webgpu' ? 'webgpu' : null;
}

/** 本次启动是否以 WebGL2 兼容模式运行（main.ts 据此给用户提示）。 */
let webgl2CompatBoot = false;
export function webgpuFallbackActive(): boolean {
    return webgl2CompatBoot;
}

/**
 * 运行期 WebGPU 健康守卫：只在「渲染实际坏掉」时才降级（2026-10-17 语义
 * 修正）——错误爆发须连续持续 ≥10s（一过性爆发忽略），devicelost
 * 需未恢复或密集风暴（引擎能自愈的孤立丢失不算）。命中 → 记 flag +
 * loader 文案 + 整页重载（下轮 boot 走 WebGL2）。仅在 WebGPU 会话安装；
 * 显式 ?renderer=webgpu 时不装（用户要求强行 WebGPU，不自动回退）。
 */
function installWebgpuHealthGuard(device: GraphicsDevice): void {
    // 幂等：dev 探针重复安装/重复调用只挂一份监听（否则阈值计数翻倍）。
    if ((device as GraphicsDevice & { __nngsGuard?: boolean }).__nngsGuard) return;
    (device as GraphicsDevice & { __nngsGuard?: boolean }).__nngsGuard = true;
    // adapter 一行日志：现场排障第一手资料（vendor/architecture/device 定位那台机器）。
    const adapterInfo = (device as GraphicsDevice & { gpuAdapter?: { info?: { vendor?: string; architecture?: string; device?: string } } }).gpuAdapter?.info;
    console.info(`[gfx] WebGPU adapter: vendor=${adapterInfo?.vendor ?? '?'} architecture=${adapterInfo?.architecture ?? adapterInfo?.device ?? '?'} (${JSON.stringify(adapterInfo ?? null)})`);

    const raw = (device as GraphicsDevice & { wgpu?: RawGpuDevice }).wgpu;
    const errorStamps: number[] = [];
    const lostStamps: number[] = [];
    // 持续性判定状态：pendingBurstAt = 首窗达标时刻（0 = 未挂起）；
    // lastErrorAt 用于识别「静默满一个窗」——爆发已结束，挂起作废。
    let pendingBurstAt = 0;
    let lastErrorAt = 0;
    // 未恢复计时器：devicelost 起 DEVICE_RESTORE_GRACE_MS 内没有 devicerestored 即判死。
    let restoreTimer: number | null = null;
    let triggered = false;
    const fallback = (reason: string): void => {
        if (triggered) return;
        triggered = true;
        // 强开白名单（第④层，2026-10-17）：本机 7 天内有过 ?renderer=webgpu
        // 稳定运行 ≥10 分钟的记录 = 人工验证过「这台机器 WebGPU 实际渲染
        // 正常」。异常信号按可自愈抖动处理——不重载、不降级，仅记录放行
        //（10-14 手机豁免的桌面同款逻辑，但需显式强开背书，不对全体桌面放宽）。
        if (webgpuForcedOkRecent()) {
            gfxGuardBeacon('whitelist-skip', reason);
            console.warn(`[gfx] WebGPU 异常信号（${reason}）；本机近期强开 WebGPU 稳定运行过，按可自愈抖动忽略（不降级不重载）`);
            return;
        }
        // 三层决策第②层：30 分钟内没有 trip 记录 → 视为瞬态，记 trip 并重载
        // 重试一次，不降级（不支持 WebGPU 的抖动一次不该钉死 WebGL2 半速）。
        const priorTrip = recentWebgpuTrip();
        if (!priorTrip && setWebgpuTrip(reason)) {
            gfxGuardBeacon('trip', reason);
            console.warn(`[gfx] WebGPU 疑似瞬态异常（${reason}），重载重试一次；30 分钟内再次触发才会降级 WebGL2`);
            const loaderText = document.getElementById('loader-text');
            if (loaderText) loaderText.textContent = '图形设备异常，正在重试…';
            window.setTimeout(() => window.location.reload(), 800);
            return;
        }
        // 第③层：二连触发（或 sessionStorage 禁写无法区分瞬态）→ 落 24h flag。
        // 原因+adapter 随 flag 落盘（localStorage: nngs.webgpu-broken-info），
        // 事后不靠复现也能查「这台机器当初为什么降级」。
        const flagReason = priorTrip ? `二连触发：${reason}（首次：${priorTrip.reason}）` : reason;
        gfxGuardBeacon('flag', flagReason);
        setWebgpuBrokenFlag(true, flagReason, `vendor=${adapterInfo?.vendor ?? '?'} arch=${adapterInfo?.architecture ?? adapterInfo?.device ?? '?'}`);
        try { sessionStorage.removeItem(WEBGPU_TRIP_KEY); } catch { /* 已落 flag，trip 清不清无所谓 */ }
        console.warn(`[gfx] WebGPU 兼容性失败（${reason}），已记录 24h 后自动重试；即将以 WebGL2 兼容模式重新加载（?renderer=webgpu 可立即重试）`);
        const loaderText = document.getElementById('loader-text');
        if (loaderText) loaderText.textContent = '检测到显卡兼容问题，正在切换兼容模式…';
        window.setTimeout(() => window.location.reload(), 800);
    };
    raw?.addEventListener?.('uncapturederror', (ev) => {
        if (triggered) return; // 已决定回退：不再累积计数
        // OOM 不算兼容性失败（换 WebGL2 救不了，场景复杂度问题走降档链路）。
        if (ev.error?.constructor?.name === 'GPUOutOfMemoryError') return;
        const now = performance.now();
        // 静默满一个窗（10s 无错误）= 上一波爆发已结束：挂起作废、下波重新
        // 起算——只有「连续不断」的错误流才可能续到确认线，隔开的孤立爆发
        // 拼不成「持续」（2026-10-17 Ampere 案例语义）。
        if (lastErrorAt > 0 && now - lastErrorAt >= GPU_ERROR_WINDOW_MS) pendingBurstAt = 0;
        lastErrorAt = now;
        errorStamps.push(now);
        while (errorStamps.length > 0 && now - errorStamps[0] > GPU_ERROR_WINDOW_MS) errorStamps.shift();
        if (errorStamps.length < GPU_ERROR_THRESHOLD) return;
        const sample = ev.error?.message?.slice(0, 160) ?? 'unknown';
        if (pendingBurstAt === 0) {
            pendingBurstAt = now;
            console.warn(`[gfx] WebGPU 错误爆发（10s 内 ≥${GPU_ERROR_THRESHOLD} 个：${sample}）；持续 ≥10s 才降级，一过性爆发忽略`);
            return;
        }
        // 确认线：阈值持续满足且已跨满一个窗（首窗达标后错误仍在来，中间无
        // ≥10s 静默）——坏管线每帧报错 ~10s 必过线；加载期爆发静默后挂起已作废。
        if (now - pendingBurstAt >= GPU_ERROR_WINDOW_MS) {
            fallback(`10s 窗口持续 ≥${GPU_ERROR_THRESHOLD} 个未捕获 GPU 错误（已持续 ${((now - pendingBurstAt) / 1000).toFixed(0)}s）：${sample}`);
        }
    });
    device.on('devicelost', () => {
        if (triggered) return;
        const now = performance.now();
        // ① 未恢复判定：宽限期内没有 devicerestored = 设备重建失败、渲染已死。
        //    计时器幂等重置（丢失叠丢失按最后一次起算——真风暴走下方②更早命中）。
        if (restoreTimer !== null) window.clearTimeout(restoreTimer);
        restoreTimer = window.setTimeout(() => {
            restoreTimer = null;
            if (!triggered) fallback(`devicelost 后 ${DEVICE_RESTORE_GRACE_MS / 1000}s 未恢复（设备重建失败）`);
        }, DEVICE_RESTORE_GRACE_MS);
        // ② 风暴判定：短窗内反复丢失（每次都恢复了也是用户可见的闪屏地狱）。
        lostStamps.push(now);
        while (lostStamps.length > 0 && now - lostStamps[0] > DEVICE_LOST_WINDOW_MS) lostStamps.shift();
        if (lostStamps.length >= DEVICE_LOST_LIMIT) {
            if (restoreTimer !== null) { window.clearTimeout(restoreTimer); restoreTimer = null; }
            fallback(`${DEVICE_LOST_WINDOW_MS / 1000}s 内 devicelost x${lostStamps.length}（丢失风暴）`);
        }
    });
    device.on('devicerestored', () => {
        // 自愈成功：撤掉未恢复判定。孤立的已恢复丢失不计入任何降级条件——
        // 这是 2026-10-17 误判案例的核心修正。
        if (restoreTimer !== null) { window.clearTimeout(restoreTimer); restoreTimer = null; }
    });
}

interface ViewportSize {
    width: number;
    height: number;
}

// 2026-10-14 帧率优化：基准 2.0→1.75（填充率立省 ~23%，弱机不再永久顶满
// 地板），地板 1.6→1.25（低帧收缩有真空间——此前 1.6 地板让动态分辨率在
// 弱机上形同虚设：一降就触底、再也回不去）。MAX 2.25 仍高于 1.75 基准，
// 实际封顶由 mobilePixelRatioCap() 决定。
const MOBILE_CANVAS_PIXEL_RATIO = 1.75;
const MOBILE_CANVAS_PIXEL_RATIO_MIN = 1.25;
const MOBILE_CANVAS_PIXEL_RATIO_MAX = 2.25;
const MOBILE_CANVAS_PIXEL_RATIO_STEP_DOWN = 0.05;
const MOBILE_CANVAS_PIXEL_RATIO_STEP_UP = 0.05;
const MOBILE_DYNAMIC_SAMPLE_SECONDS = 4;
const MOBILE_DYNAMIC_LOW_FPS = 23;
const MOBILE_DYNAMIC_HIGH_FPS = 42;
// Mobile splat budget. 1.1M (the old value) is a static greedy allocation, not
// FPS-adaptive: with mainhall's LOD2-allowed finest at 11.4M splats, the
// balancer pinned the whole scene at LOD4-6 (probe-measured: engine held
// desires on LOD5/LOD6 only), i.e. ~5-10% density everywhere — the main cause
// of mobile blur after entry. 3M lets near-field nodes reach LOD2/LOD3 while
// still bounding rendered splats for mid-range phones; tune with probe-lod.
// 2026-09-22 真机帧率修复：3M → 1.2M，配合渲染上限 LOD2（loadGaussianScene
// 的 MOBILE_SHARED_HIGH_LOD_INDEX 1→2，此前近场精化到 LOD1≈桌面级画质，
// 手机帧率大跌）。1.2M 让 LOD2 只在镜头几米内、中距 LOD3/4、远处压最粗，
// 整条距离曲线比桌面（12M/LOD0）低约两档，渲染 splat 量比 3M 时期再降
// ~60%；minPixelSize 1→2 / minContribution 1.5→3（对齐桌面）额外裁掉
// 远处过小贡献的粒子 —— “太远也渲染高清”即由此消除。
// 2026-09-22 晚：1.2M → 2M。真机反馈手机端看不到桌面那种「近细远粗 + 朝向
// 切换」的梯度——1.2M 被脚下 LOD1 环吃光后，其余视场的 optimal 直接塌到
// 粗档，转身换面都是同一片粗，朝向变化无从感知（桌面 12M 买出整条距离曲线
// LOD0≤27m/LOD1≤90m/LOD2≤147m 才有梯度可看）。2M 让 LOD2/LOD3 中间档
// 重新进入平衡器分配范围，配合分环预载把对应档位数据铺到位；LOD0 仍被
// lodRangeMin 禁用。帧率兜底：动态像素比 <23FPS 自动降分辨率；若真机仍
// 明显掉帧，优先回调此值（1.6M/1.4M），不动上限与环表。
// 2026-10-14：2M → 1.4M，同时渲染上限 LOD3 回滚到 LOD2（loadGaussianScene/
// main.ts）。关键认知：预算是「目标」而非上限——平衡器近端优先填充直到
// 打满，laomendong2 场景总量 LOD2=5.6M / LOD3=2.8M 都远超预算，两档实渲染
// 都 ≈2M splats/帧；上限降到 LOD3 只减密度（40%→20%）不减每帧成本，真机
// 帧率没救回来还白丢一档画质（日志证实：LOD3 生效、帧率依旧）。真正减负
// 的是预算本身：1.4M ≈ 2M 的 70%，光栅+排序按 splat 数付费同步降 30%。
const MOBILE_GSPLAT_SPLAT_BUDGET = 1400000;
/** tier 1（单次崩溃恢复，stabilityGuard）的预算与 LOD 上限组合：1.4M→1.2M；
 *  LOD 上限 2026-10-14 起回到 tier 抬升语义（loadGaussianScene：
 *  LOD2+tier 封顶 LOD3），tier1 上限 LOD3——「被系统杀掉重启自动降一级」
 *  的温和档（预算只小降，LOD 粗一档）。 */
const MOBILE_GSPLAT_TIER1_SPLAT_BUDGET = 1200000;
/** tier 2（全面降档，stabilityGuard）：1.4M→600k，配合 LOD 上限收紧
 *  （loadGaussianScene）把流式解码与 VRAM 峰值一起压下来——2026-09-28 iPhone
 *  页面死亡循环的直接对症档。600k≈1.4M 的 43%，实测档位曲线（createApp 顶注）
 *  上近场仍保有 LOD3-4，可玩但明显粗。 */
const MOBILE_GSPLAT_DEGRADED_SPLAT_BUDGET = 600000;
// Desktop splat budget. These scenes need 14.07M (laomendong2) to 28.43M
// (mainhall / meihuashan) splats for full detail, so the engine's 1M default —
// what `splatBudget = 0` resolves to — starved even the near field.
//
// Measured on laomendong2 (furthest distance at which each LOD is still reached,
// camera at the spawn; the scene is 147m across):
//     budget  1M → LOD3 only within 1.7m, nothing finer anywhere
//     budget  8M → LOD0 ≤ 6.3m,  LOD1 ≤ 20.3m, LOD2 ≤ 61.4m
//     budget 12M → LOD0 ≤ 26.6m, LOD1 ≤ 90.0m, LOD2 ≤ 147m
//     budget 14.1M+ → whole scene pinned at LOD0 (its 14,065,780 total)
//
// 2026-09-19: 28.5M pinned mainhall at LOD0 everywhere (28.43M splats/frame
// → low desktop FPS; F8 LOD colorize solid red, no distance falloff at all).
// The balancer treats the budget as a TARGET: when the distance-curve total
// is below budget it refines nodes near→far toward LOD0 until filled, so an
// oversized budget lets the LOD0 fill creep across the whole scene over time
// (F8: far field slowly turning red).
// 2026-09-20: 8M → 12M. At 8M high detail stopped too close (laomendong2:
// LOD0 ≤ 6.3m / LOD1 ≤ 20.3m); probes run at a steady 60 FPS with headroom,
// so spend it on detail distance: 12M measured LOD0 ≤ 26.6m / LOD1 ≤ 90.0m /
// LOD2 ≤ 147m on laomendong2, VRAM commit ~420 MB (12M → capacity 13.8M →
// textureSize 3715). Still far below the 28.43M full-scene pin that broke FPS.
// 28.5M is the ceiling, not an arbitrary large number: it is just above the
// heaviest scene's full-detail count, so the balancer's `totalFinestCount <=
// budget` short-circuit pins *every* scene at LOD0 at every distance and no LOD
// degradation is possible anywhere. Raising it further is provably a no-op —
// the simulator renders byte-identical splat counts at 100M (laomendong2
// 14,065,780; mainhall 28,431,389) — while the work buffer keeps growing,
// because its render targets are sized from the allocator's *capacity*, not
// from actual use: GSplatWorldState.applyAllocations does
// `textureSize = ceil(sqrt(allocator.capacity))` and the allocator is built as
// `BlockAllocator(ceil(budget * 1.15))` up front. So the budget is a VRAM
// commitment regardless of what gets drawn:
//      12M  → capacity 13.8M → textureSize 3715 → ~420 MB
//    28.5M  → capacity 32.8M → textureSize 5725 → ~1000 MB
//     100M  → capacity 115M  → textureSize 10724 → ~3500 MB (device loss risk)
const DESKTOP_GSPLAT_SPLAT_BUDGET = 12000000;
const MOBILE_GSPLAT_MIN_PIXEL_SIZE = 2;
const MOBILE_GSPLAT_MIN_CONTRIBUTION = 3;
// 2026-09-22 晚：0.5 → 0.8，配合「视线前方最高精度」策略。foveation 按屏幕
// 位置加权 LOD：注视中心细、周边/脚下粗，把 2M 预算的额度从周边挪给视线
// 方向（与分环预载的前方细/侧后粗一档、lodBehindPenalty 三者同向）。桌面
// 恒 0=关：屏幕大、周边也是有效视野，加权不划算；手机屏幕小周边余光不重要。
const MOBILE_GSPLAT_FOVEATION_STRENGTH = 0.8;
const MOBILE_GSPLAT_FOVEATION_CENTER = 0.42;

let mobileCanvasPixelRatio = MOBILE_CANVAS_PIXEL_RATIO;
/** 降级档像素比上限：动态分辨率的上限从 2.0 压到 1.0（CSS 像素 1:1），
 *  填充率与纹理带宽同步减半；下限仍由动态分辨率逻辑自行收缩。仅 tier 2
 *  （全面降档）启用；tier 1 只动 LOD/预算，分辨率交动态分辨率自适应。
 *  降档判定来自 stabilityGuard 的 boot 死亡信号（mobileLowSpec 场景档自
 *  09-30 起为 tier 1，不再触发 1.0 像素比——真机反馈顶格档过糊）。 */
const mobilePixelRatioCap = () => (mobileDegradeActive() ? 1.0 : MOBILE_CANVAS_PIXEL_RATIO);
/** 分级预算（stabilityGuard tier）：0=1.4M / 1=1.2M / 2=600k。 */
const mobileSplatBudgetCap = () => {
    const level = mobileTierLevel();
    return level >= 2 ? MOBILE_GSPLAT_DEGRADED_SPLAT_BUDGET
        : level === 1 ? MOBILE_GSPLAT_TIER1_SPLAT_BUDGET
        : MOBILE_GSPLAT_SPLAT_BUDGET;
};
/** 供 applyMobileSceneTier 复用 resize/预算设置（createApp 后非空）。 */
let currentApp: Application | null = null;
let dynamicResolutionTime = 0;
let dynamicResolutionFrames = 0;
let mobileSceneReady = false;

export async function createApp(canvas: HTMLCanvasElement): Promise<AppContext> {
    // markBoot 已前移到 main() 第一行（早于任何 await，极速死亡循环也要计数）；
    // 此处只按判定结果收紧 boot 档位。
    if (mobileTierLevel() > 0) {
        mobileCanvasPixelRatio = Math.min(mobileCanvasPixelRatio, mobilePixelRatioCap());
        console.info(`[stability] tier boot: level=${mobileTierLevel()} splatBudget=${mobileSplatBudgetCap()} pixelRatioCap=${mobilePixelRatioCap()}`);
    }
    // 渲染后端选择（三层决策，见文件头「WebGPU 兼容性守卫」注释）：
    // ① 静态支持检测——不支持（无 API/拿不到 adapter）直接 WebGL2，这不是
    //    「兼容模式」：不落 flag、不给提示，下次 boot 重测。
    // ② 支持则 WebGPU（24h flag 激活时例外，直接 WebGL2 兼容模式）。
    // ③ 运行期守卫负责「支持但真坏」的机器：瞬态重试一次，二连才落 flag。
    // ?renderer=webgpu 显式清除 flag 强行 WebGPU（同时不装守卫）。
    const rendererOverrideValue = rendererOverride();
    if (rendererOverrideValue === 'webgl2') setWebgpuBrokenFlag(true, '?renderer=webgl2 手动强制');
    else if (rendererOverrideValue === 'webgpu') setWebgpuBrokenFlag(false);
    webgl2CompatBoot = webgpuBrokenFlag();
    let useWebgl2 = webgl2CompatBoot;
    const support = await detectWebgpuSupport();
    if (!support.supported) {
        useWebgl2 = true;
        console.info(`[gfx] WebGPU 不支持（${support.reason}），本次直接使用 WebGL2`);
    }
    // 手机端豁免（2026-10-14 定论）：实测 XWEB150 手机 WebGPU 本身健康
    //（?renderer=webgpu 强开即回 20+ 帧），但每个正常会话都被运行期守卫
    // 触发（瞬态校验错误 / 流送期显存压力 devicelost，引擎均可自恢复），
    // v2 单次即降级、v3 二连也拦不住「每场必发」→ 钉死 WebGL2 半速一整天。
    // 手机真坏个案为零、误伤代价巨大 → 手机不装运行期守卫、不认既有 flag
    // （v1/v2 时代落下的直接清除），只保留支持检测与 ?renderer=webgl2 手动
    // 通道；桌面全量保留三层（真坏案例在桌面）。
    const mobileGuardExempt = isMobileRuntime();
    if (mobileGuardExempt && rendererOverrideValue !== 'webgl2' && webgl2CompatBoot) {
        gfxGuardBeacon('mobile-flag-cleared', '手机端豁免：清除 v1/v2 时代守卫 flag');
        console.info('[gfx] 手机端豁免 WebGPU 守卫：清除既有 flag，按支持检测选择后端（真有问题用 ?renderer=webgl2 手动降级）');
        setWebgpuBrokenFlag(false);
        webgl2CompatBoot = false;
        useWebgl2 = !support.supported;
    }
    const device = await createGraphicsDevice(canvas, {
        deviceTypes: [useWebgl2 ? DEVICETYPE_WEBGL2 : DEVICETYPE_WEBGPU],
        antialias: false,
        depth: true,
        powerPreference: 'high-performance'
    });
    if (device.isWebGPU === true && rendererOverrideValue !== 'webgpu' && !mobileGuardExempt) {
        installWebgpuHealthGuard(device);
    } else if (webgl2CompatBoot) {
        gfxGuardBeacon('compat-boot', 'flag 激活，WebGL2 兼容 boot');
        console.info('[gfx] WebGPU 已因兼容性问题在本浏览器暂时禁用（24h 后自动重试），使用 WebGL2（带 ?renderer=webgpu 可立即重试）');
    }
    // 强开白名单记录（与守卫互斥，见 WEBGPU_FORCED_OK_KEY 注释）：显式
    // ?renderer=webgpu 的会话连续存活 ≥10 分钟才落盘——中途关页/驱动级
    // 崩溃的坏机器天然到不了 10 分钟。
    if (rendererOverrideValue === 'webgpu' && device.isWebGPU === true) {
        const forcedInfo = (device as GraphicsDevice & { gpuAdapter?: { info?: { vendor?: string; architecture?: string; device?: string } } }).gpuAdapter?.info;
        const forcedAdapter = `vendor=${forcedInfo?.vendor ?? '?'} arch=${forcedInfo?.architecture ?? forcedInfo?.device ?? '?'}`;
        window.setTimeout(() => markWebgpuForcedOk(forcedAdapter), 10 * 60 * 1000);
    }
    // dev/e2e 探针（同 __app/__rig 约定，生产构建静态消除）：headless 假 GL
    // 环境拿不到真 WebGPU 设备，守卫逻辑用注入假 wgpu 事件源 + 本探针触发验证。
    if (import.meta.env.DEV) {
        (window as unknown as { __gfxGuard?: () => boolean }).__gfxGuard = () => {
            installWebgpuHealthGuard(device);
            return true;
        };
    }

    const app = new Application(canvas, {
        graphicsDevice: device
    });
    currentApp = app;

    // 尺寸策略按形态分流：竖屏持机走虚拟横屏（画面整体转 90°，loading 起
    // 即横屏显示；尺寸跟 visualViewport，输入期竖屏覆盖见 InputPortraitMode
    // 与 setMobileInputPortrait）；真横屏（物理旋转/锁屏成功）尺寸只跟布局视口——
    // 系统键盘只缩 visualViewport 时（Chrome 108+ 默认 + 本站 meta 显式
    // resizes-visual；iOS 布局视口从不缩）innerHeight 不动，UI 天然不被
    // 压缩；微信 XWeb/部分国产浏览器按 resizes-content 把布局视口一起压
    // 小——getViewportSize 的键盘冻结兜住这类浏览器，冻结期间
    // syncKeyboardOffset 再把聚焦输入框平移进键盘上方可见区。
    // 旋转重算挂满四路信号：部分浏览器只发其中一路（XWeb 常只发
    // orientationchange，iOS 反之），且 innerWidth/Height 在旋转动画窗口内
    // 可能短暂报旧值——统一多拍延迟重算兜底。
    const resize = () => resizeCanvasForDevice(app);
    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('orientationchange', () => {
        resize();
        window.setTimeout(resize, 120);
        window.setTimeout(resize, 380);
        window.setTimeout(resize, 900);
    });
    window.visualViewport?.addEventListener('resize', () => {
        resize();
        window.setTimeout(resize, 380);
    });
    (screen.orientation as ScreenOrientation | undefined)?.addEventListener?.('change', () => {
        resize();
        window.setTimeout(resize, 380);
    });
    // 键盘弹出/收起时重判冻结态：focusin 晚一拍（键盘常在焦点落地后才压
    // 视口），focusout 立即 + 多拍（收起时高度恢复需要解冻重算）。
    window.addEventListener('focusin', () => window.setTimeout(resize, 300));
    window.addEventListener('focusout', () => {
        resize();
        window.setTimeout(resize, 300);
        window.setTimeout(resize, 900);
    });
    app.on('update', (dt: number) => updateDynamicMobileResolution(app, dt));
    eventBus.on('SCENE_LOAD_START', (event) => {
        if (!isMobileRuntime()) return;
        if (event.progress !== 0) return;
        mobileSceneReady = false;
        dynamicResolutionTime = 0;
        dynamicResolutionFrames = 0;
        mobileCanvasPixelRatio = Math.min(mobileCanvasPixelRatio, mobilePixelRatioCap());
        resizeCanvasForDevice(app);
        console.info(`[mobile] dynamic pixel ratio reset for scene load=${mobileCanvasPixelRatio.toFixed(2)}`);
    });
    eventBus.on('SCENE_READY', () => {
        if (!isMobileRuntime()) return;
        mobileSceneReady = true;
        dynamicResolutionTime = 0;
        dynamicResolutionFrames = 0;
    });

    // Render layers: Splat / Occlusion / World / Avatar / Overlay (see LayerManager).
    const layers = initLayers(app);

    // Streamed-gsplat tuning for fast camera turns:
    //   - renderer: prefer WebGPU GPU-side sort. On WebGL2 this resolves back
    //     to CPU sort, so the app still starts on browsers without WebGPU.
    //   - radialSorting: reduces rotation-induced sort artifacts while looking
    //     around in place.
    //   - lodUnderfillLimit/cooldownTicks: keep the previous streamed LOD
    //     fallback behavior for non-resident chunks.
    //   - lodBehindPenalty/lodUpdateAngle: let geometry behind the camera drop
    //     to a coarser LOD so its splats are reclaimed for what is in front.
    //     Both platforms want this; it is set for desktop as well, matching the
    //     supersplat-viewer preset.
    const mobile = isMobileRuntime();
    app.scene.gsplat.renderer = GSPLAT_RENDERER_RASTER_GPU_SORT;
    app.scene.gsplat.radialSorting = true;
    //   - lodUnderfillLimit: how many levels coarser than optimal a node may
    //     keep DISPLAYING while streaming. 3 (was 2 since 2026-09-20): with the
    //     LOD3 entry preload (2026-09-21), spawn nodes run optimal=LOD0 under the
    //     12M curve, and selectDesiredLodIndex only considers resident LODs in
    //     [optimal, optimal+limit] — limit 2 excluded LOD3, so newly-visible
    //     nodes had NOTHING resident in range and stayed invisible (blank spawn
    //     surroundings). Limit 3 puts the preloaded LOD3 back in range; it also
    //     keeps the graded far LOD4 (30-60m band, optimal≈1) displayable.
    app.scene.gsplat.lodUnderfillLimit = mobile ? 10 : 3;
    app.scene.gsplat.splatBudget = mobile
        ? mobileSplatBudgetCap()
        : DESKTOP_GSPLAT_SPLAT_BUDGET;
    app.scene.gsplat.minPixelSize = mobile ? MOBILE_GSPLAT_MIN_PIXEL_SIZE : 2;
    app.scene.gsplat.minContribution = mobile ? MOBILE_GSPLAT_MIN_CONTRIBUTION : 3;
    app.scene.gsplat.foveationStrength = mobile ? MOBILE_GSPLAT_FOVEATION_STRENGTH : 0;
    app.scene.gsplat.foveationCenter = mobile ? MOBILE_GSPLAT_FOVEATION_CENTER : 0.3;
    app.scene.gsplat.lodUpdateAngle = mobile ? 60 : 90;
    app.scene.gsplat.lodBehindPenalty = 5;
    app.scene.gsplat.colorUpdateAngle = mobile ? 4 : 2;
    app.scene.gsplat.alphaClip = 1 / 255;
    app.scene.gsplat.cooldownTicks = 60000;
    console.info(`[render] device=${device.deviceType}, gsplatRenderer=${app.scene.gsplat.currentRenderer}`);

    // Camera rig: PlayerRoot (position) + Camera (rotation).
    const cameraRoot = new Entity('PlayerRoot');
    app.root.addChild(cameraRoot);

    const camera = new Entity('Camera');
    cameraRoot.addChild(camera);
    camera.addComponent('camera', {
        // 天空盒未就绪期间（首装后装/传送卸载后重装/未配置天空盒）的清屏色。
        // 原近黑 0.02 在后装窗口被当成「黑屏/加载卡死」上报；白色与天空
        // 亮调接近，过渡到真实天空盒也更平滑。两平台同一台相机。
        clearColor: new Color(1, 1, 1),
        fov: 75
    });
    // Render skybox, default World (gsplat), and our custom layers.
    camera.camera!.layers = layers.allIds;

    // Directional light (gaussian splats are unlit, but lights help any meshes).
    const light = new Entity('Light');
    light.setEulerAngles(45, 45, 0);
    light.addComponent('light', {
        type: 'directional',
        intensity: 1
    });
    app.root.addChild(light);
    // Illuminate the character (Avatar), custom meshes (World), and markers (Overlay); splats are unlit.
    light.light!.layers = [layers.avatarOpaque.id, layers.avatarTransparent.id, layers.world.id, layers.overlay.id];

    app.scene.ambientLight.set(0.4, 0.42, 0.5);

    app.start();

    // 引擎已知竞态兜底（详见 gsplatEnginePatch 文件头）：world 在首个场景
    // 首帧才创建，补丁内部轮询到命中为止；必须早于任何场景卸载发生。
    installGsplatEnginePatches(app);

    // 会话稳定存活满 2 分钟即视为健康：清空 boot 计数，下次进入恢复标准画质
    // （本会话保持降级档不变，中途放宽会在半途二次抬高峰值）。
    setTimeout(() => markStableSession(), STABLE_SESSION_MS);

    return { app, cameraRoot, camera };
}

export function isMobileRuntime(): boolean {
    const viewport = getViewportSize();
    return window.matchMedia('(pointer: coarse)').matches || viewport.width < 760;
}

/** 键盘冻结基准（上次无键盘时的布局视口）与冻结态，见 getViewportSize。 */
let stableViewport: ViewportSize | null = null;
let keyboardFrozen = false;
/** 高度骤降阈值（px）：超过视为键盘弹出，小于视为地址栏收起等小波动。 */
const KEYBOARD_RESIZE_PX = 110;

function isEditableActive(): boolean {
    const el = document.activeElement;
    if (!(el instanceof HTMLElement)) return false;
    return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
}

/**
 * 布局视口尺寸。虚拟横屏期（竖屏持机）沿用原始行为：跟 visualViewport
 * ——代理键盘条按可见区贴键盘上沿，冻结/平移在旋转坐标系里都不成立。
 * 真横屏/竖屏原生布局用 innerWidth/innerHeight，带键盘冻结：
 * - resizes-visual 类（Chrome 108+/iOS）：键盘只缩 visualViewport，
 *   innerHeight 不动，直接返回即「UI 不被键盘压缩」。
 * - resizes-content 类（微信 XWeb、部分国产浏览器，meta 拦不住）：键盘把
 *   布局视口一起压小。检测「可编辑聚焦 + 宽度不变 + 高度骤降」时冻结在
 *   弹出前尺寸——UI 高度保持不变，键盘浮在上层遮住底部；高度恢复 / 焦点
 *   离开 / 宽度变化（旋转）即解冻采用新尺寸。
 */
function getViewportSize(): ViewportSize {
    if (document.getElementById('app-root')?.classList.contains('mobile-virtual-landscape')) {
        // 虚拟横屏：同步清掉冻结态，转回真横屏时从当前实测重新起算。
        keyboardFrozen = false;
        stableViewport = null;
        const visualViewport = window.visualViewport;
        return {
            width: Math.max(1, Math.round(visualViewport?.width ?? window.innerWidth)),
            height: Math.max(1, Math.round(visualViewport?.height ?? window.innerHeight))
        };
    }
    const raw = {
        width: Math.max(1, Math.round(window.innerWidth)),
        height: Math.max(1, Math.round(window.innerHeight))
    };
    if (stableViewport === null) stableViewport = raw;
    const widthChanged = Math.abs(raw.width - stableViewport.width) > 80;
    if (!keyboardFrozen) {
        // 阈值取「固定 px 与 18% 高度」较大者：键盘吃掉 40%+ 高度，地址栏
        // 收起只有 ~10%，比例下限在竖屏高视口上比固定 px 分离得更开。
        const dropThreshold = Math.max(KEYBOARD_RESIZE_PX, stableViewport.height * 0.18);
        const keyboardDropped = stableViewport.height - raw.height > dropThreshold;
        if (isEditableActive() && !widthChanged && keyboardDropped) {
            keyboardFrozen = true;
            console.info(`[viewport] keyboard freeze: keep ${stableViewport.width}x${stableViewport.height} (raw ${raw.width}x${raw.height})`);
        } else {
            stableViewport = raw;
        }
    }
    if (!keyboardFrozen) return raw;
    const recovered = raw.height >= stableViewport.height - KEYBOARD_RESIZE_PX / 2;
    if (widthChanged || recovered || !isEditableActive()) {
        keyboardFrozen = false;
        stableViewport = raw;
        return raw;
    }
    return stableViewport;
}

/** 输入期竖屏覆盖（InputPortraitMode 驱动）：虚拟横屏下文本输入框聚焦时
 *  整个展示切真竖屏——键盘与 UI 同向（系统键盘永远按物理方向弹出，旋转
 *  布局里按键是横躺的，代理输入条只救了输入框救不了按键）。 */
let mobileInputPortrait = false;

/** 开/关输入期竖屏：重排 canvas 与类，真实输入框不动（类切换不掉焦点，
 *  键盘不掉）。输入中途视口真的转成横屏（无方向锁用户物理旋转）时，
 *  resizeCanvasForDevice 会解除本覆盖。 */
export function setMobileInputPortrait(active: boolean): void {
    if (mobileInputPortrait === active) return;
    mobileInputPortrait = active;
    if (currentApp) resizeCanvasForDevice(currentApp);
}

function resizeCanvasForDevice(app: Application): void {
    const mobile = isMobileRuntime();
    const viewport = getViewportSize();
    // 输入期视口真转横屏：覆盖失去意义（键盘已按真横屏方向弹出），解除并
    // 跟随真实方向，避免「视口横屏却强制竖屏布局」的错位。
    if (mobileInputPortrait && viewport.width > viewport.height) mobileInputPortrait = false;
    // 虚拟横屏（竖屏持机）：宽高对调按横屏渲染，#app-root 由 CSS 旋转 90°
    // 显示——loading 屏起整个界面即横屏，首次触摸再尝试真锁横（main.ts）。
    // 输入期竖屏覆盖在场时压制虚拟横屏判定：按真竖屏尺寸布局。
    const virtualLandscape = mobile && !mobileInputPortrait && viewport.height > viewport.width;
    const width = virtualLandscape ? viewport.height : viewport.width;
    const height = virtualLandscape ? viewport.width : viewport.height;
    const pixelRatio = Math.min(window.devicePixelRatio || 1, mobile ? mobileCanvasPixelRatio : 2);
    const root = document.getElementById('app-root');
    root?.classList.toggle('mobile-runtime', mobile);
    root?.classList.toggle('mobile-virtual-landscape', virtualLandscape);
    root?.classList.toggle('mobile-native-landscape', mobile && !virtualLandscape && !mobileInputPortrait);
    root?.classList.toggle('mobile-input-portrait', mobile && mobileInputPortrait);
    root?.style.setProperty('--app-width', `${width}px`);
    root?.style.setProperty('--app-height', `${height}px`);
    root?.style.setProperty('--render-scale', `${pixelRatio}`);
    root?.setAttribute('data-render-scale', pixelRatio.toFixed(2));
    root?.setAttribute('data-canvas-size', `${Math.floor(width * pixelRatio)}x${Math.floor(height * pixelRatio)}`);
    app.graphicsDevice.maxPixelRatio = 1;
    app.setCanvasResolution(RESOLUTION_FIXED, Math.floor(width * pixelRatio), Math.floor(height * pixelRatio));
    app.resizeCanvas(width, height);
    syncKeyboardOffset(height);
}

/** 键盘遮挡补偿当前值（px，0=未平移）。量测时用它还原元素自然位置，
 *  避免上一轮平移叠进 rect 反复累计。 */
let keyboardOffsetY = 0;

/**
 * 键盘遮挡补偿：键盘冻结/浮动键盘只盖不改布局后，浏览器原本「压缩视口 +
 * 滚动让输入框可见」的机制没了，聚焦的输入框可能留在键盘后面。这里在
 * resize 重算时顺带把 #app-root 整体上移，让聚焦元素落到键盘上方可见区
 * （visualViewport 的 offsetTop+height 就是可见区底边，两类浏览器通用：
 * resizes-visual 只缩 vv、resizes-content 冻结后 vv 同步缩到可见高）。
 * 键盘收起/失焦/可见区恢复时清零平移。
 */
function syncKeyboardOffset(layoutHeight: number): void {
    const root = document.getElementById('app-root');
    if (!root) return;
    const clear = () => {
        if (keyboardOffsetY !== 0) {
            keyboardOffsetY = 0;
            root.style.transform = '';
        }
    };
    // 虚拟横屏期输入走代理键盘条（自身贴键盘），旋转坐标系里平移量不成立；
    // 且必须清内联 transform——内联值会盖掉类上的 rotate(90deg) 显示。
    if (root.classList.contains('mobile-virtual-landscape')) {
        clear();
        return;
    }
    const el = document.activeElement;
    if (!(el instanceof HTMLElement)
        || (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA' && !el.isContentEditable)) {
        clear();
        return;
    }
    // 可见区底边明显低于当前布局高度（差 >50px 视为键盘在遮挡）才需要补偿。
    // 基准是布局高度而非 innerHeight：XWeb 冻结期 innerHeight 已缩到可见高，
    // 拿它当基准永远判不出遮挡。
    const vv = window.visualViewport;
    if (!vv) { clear(); return; }
    const visibleBottom = vv.offsetTop + vv.height;
    if (visibleBottom >= layoutHeight - 50) { clear(); return; }
    const rect = el.getBoundingClientRect();
    const MARGIN = 12;
    // rect 已含当前平移（translateY 负值上移）：加回偏移还原自然位置，
    // 再算需要抬多少——多拍 resize 重算时结果稳定，不会来回振荡。
    const naturalBottom = rect.bottom + keyboardOffsetY;
    const overflow = naturalBottom - (visibleBottom - MARGIN);
    if (overflow > 0) {
        // 上限：不把元素顶抬出可见区顶（元素比可见区高时优先显示其顶部）。
        keyboardOffsetY = Math.min(overflow, Math.max(0, naturalBottom - rect.height));
        root.style.transform = `translateY(${Math.round(-keyboardOffsetY)}px)`;
    } else {
        clear();
    }
}

/** 场景级性能档位（switchTo 在 manifest 已知、开始流送之前调用）：按
 *  stabilityGuard 当前降档级别重设 splat 预算与像素比。重载场景
 *  （manifest.mobileLowSpec，如 meihuashan 784MB 桌面级 LOD 树）手机端
 *  无条件 tier 1（LOD 上限 2/1.2M；09-30 起，原顶格 tier 2 过糊——崩溃
 *  证据仍可推到 tier 2=600k/像素比 1）；普通场景按 boot 信号分级（tier 1
 *  单次崩溃 1.2M，无信号 1.4M/1.75）——传送门往返轻重场景时档位跟随切换。
 *  桌面恒 12M（无降档语义）。 */
export function applyMobileSceneTier(): void {
    if (!currentApp) return;
    const mobile = isMobileRuntime();
    const budget = mobile
        ? mobileSplatBudgetCap()
        : DESKTOP_GSPLAT_SPLAT_BUDGET;
    currentApp.scene.gsplat.splatBudget = budget;
    if (mobile) {
        mobileCanvasPixelRatio = Math.min(mobileCanvasPixelRatio, mobilePixelRatioCap());
        resizeCanvasForDevice(currentApp);
    }
    console.info(`[stability] scene tier: level=${mobileTierLevel()} splatBudget=${budget} pixelRatioCap=${mobilePixelRatioCap().toFixed(1)}`);
}

function updateDynamicMobileResolution(app: Application, dt: number): void {
    if (!isMobileRuntime()) {
        dynamicResolutionTime = 0;
        dynamicResolutionFrames = 0;
        return;
    }
    if (!mobileSceneReady) return;

    dynamicResolutionTime += dt;
    dynamicResolutionFrames++;
    if (dynamicResolutionTime < MOBILE_DYNAMIC_SAMPLE_SECONDS) return;

    const averageFps = dynamicResolutionFrames / dynamicResolutionTime;
    // 动态升降两侧都以 mobilePixelRatioCap 为界：降档（boot 死亡信号或
    // mobileLowSpec 重载场景）时上限 1.0，回升路径不得越过它把降档顶回去；
    // 下限同理取 min（1.25 与 1.0 中取小），否则低帧收缩会把 1.0 抬回 1.25。
    const ratioCap = mobilePixelRatioCap();
    const ratioFloor = Math.min(MOBILE_CANVAS_PIXEL_RATIO_MIN, ratioCap);
    let nextPixelRatio = mobileCanvasPixelRatio;
    if (averageFps < MOBILE_DYNAMIC_LOW_FPS) {
        nextPixelRatio = Math.max(ratioFloor, mobileCanvasPixelRatio - MOBILE_CANVAS_PIXEL_RATIO_STEP_DOWN);
    } else if (averageFps > MOBILE_DYNAMIC_HIGH_FPS) {
        nextPixelRatio = Math.min(Math.min(ratioCap, MOBILE_CANVAS_PIXEL_RATIO_MAX), mobileCanvasPixelRatio + MOBILE_CANVAS_PIXEL_RATIO_STEP_UP);
    }
    nextPixelRatio = Math.min(ratioCap, nextPixelRatio);

    dynamicResolutionTime = 0;
    dynamicResolutionFrames = 0;
    if (nextPixelRatio === mobileCanvasPixelRatio) return;
    mobileCanvasPixelRatio = nextPixelRatio;
    resizeCanvasForDevice(app);
    console.info(`[mobile] dynamic pixel ratio=${mobileCanvasPixelRatio.toFixed(2)}, avgFps=${averageFps.toFixed(1)}`);
}
